import type { WorkItemDto } from '@customTypes/types';
import { Role, WorkItemStatus, WorkItemTypes } from '@customTypes/types';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Space } from '@mantine/core';
import { useEffect, useState } from 'react';

import { CreateConversationButton } from '@/features/chat/modules';
import { useArtistWorkItemsListQuery } from '@/features/work/api/artist-work-items-list';
import { NotFoundWorkItem } from '@/features/work/components/not-found-work-item/NotFoundWorkItem';
import { ClubFullProfile } from '@/features/work/view/club-full-profile/ClubFullProfile';
import { PageLoading } from '@/pages/PageLoading';
import { usePageOffset } from '@/shared/hooks';
import { translate } from '@/shared/i18n/translate';
import { useProfileStore } from '@/shared/store/profileStore';
import { LBButton, LBFixedSection } from '@/shared/ui-kit/components';

import { interactionWorkItemsStatusFilters } from '../constants/filterStatuses';
import { ManageCurrentContractContainer } from '../modules/manage-current-contract/ManageCurrentContractContainer';
import { ManageWorkInviteViewContainer } from '../modules/manage-work-invite/ManageWorkInviteContainer';
import { SendWorkRequestContainer } from '../modules/send-work-request-view/SendWorkRequestContainer';

export const ClubInteractionContainer = ({
  workItemId,
  artistId,
  clubId
}: {
  workItemId?: string;
  artistId: string;
  clubId?: string;
}) => {
  const { profile } = useProfileStore();
  const { offset, ref } = usePageOffset();
  const [currentView, setCurrentView] = useState<
    'send-request' | 'manage-invite' | 'manage-contract'
  >('send-request');

  const [currentWorkItem, setCurrentWorkItem] = useState<WorkItemDto | null>(
    null
  );
  const clubProfileId = clubId ?? currentWorkItem?.clubProfileId.toString()!;

  const workItems = useArtistWorkItemsListQuery({
    profileId: artistId,
    status: interactionWorkItemsStatusFilters
  });

  useEffect(() => {
    if (workItems.data?.data && clubId) {
      const item = workItems.data?.data.filter(
        (item) =>
          item.clubProfileId === +clubId &&
          (item.status === WorkItemStatus.Accepted ||
            item.status === WorkItemStatus.Pending ||
            item.status === WorkItemStatus.ClubSetEndDate ||
            item.status === WorkItemStatus.ContractRequestedEndDate ||
            item.status === WorkItemStatus.ContractOngoing)
      );
      if (item.length) {
        return setCurrentWorkItem(item[0]);
      }
      return setCurrentWorkItem(null);
    }

    if (workItems.data?.data && workItemId) {
      const item = workItems.data?.data.filter(
        (item) =>
          item.workItemId === +workItemId &&
          (item.status === WorkItemStatus.Accepted ||
            item.status === WorkItemStatus.Pending ||
            item.status === WorkItemStatus.ClubSetEndDate ||
            item.status === WorkItemStatus.ContractRequestedEndDate ||
            item.status === WorkItemStatus.ContractOngoing)
      );
      if (item.length) {
        return setCurrentWorkItem(item[0]);
      }
      return setCurrentWorkItem(null);
    }

    setCurrentWorkItem(null);
  }, [workItems.data?.data, artistId, workItemId]);

  useEffect(() => {
    if (
      currentWorkItem &&
      currentWorkItem.workItemType === WorkItemTypes.Request &&
      currentWorkItem.status === WorkItemStatus.Pending
    ) {
      setCurrentView('send-request');
    }

    if (
      currentWorkItem &&
      currentWorkItem.workItemType === WorkItemTypes.Invite &&
      currentWorkItem?.status === WorkItemStatus.Pending
    ) {
      setCurrentView('manage-invite');
    }

    if (
      currentWorkItem &&
      (currentWorkItem?.status === WorkItemStatus.Accepted ||
        currentWorkItem?.status === WorkItemStatus.ContractOngoing ||
        currentWorkItem?.status === WorkItemStatus.ContractRequestedEndDate ||
        currentWorkItem?.status === WorkItemStatus.ClubSetEndDate)
    ) {
      setCurrentView('manage-contract');
    }
  }, [currentWorkItem]);

  if (workItems.isLoading) return <PageLoading />;

  return (
    <div>
      {currentWorkItem || clubId ? (
        <div>
          {clubProfileId && (
            <>
              <ClubFullProfile clubId={clubProfileId} />
              <Space h={offset} />
            </>
          )}
          <LBFixedSection
            ref={ref}
            direction='column'
            gap={8}
            p={16}
            style={{
              backgroundColor: '#ffff',
              borderRadius: '8px',
              transform: 'translate(-50%)'
            }}
          >
            {currentView === 'send-request' && clubProfileId && (
              <SendWorkRequestContainer
                artistId={artistId}
                clubId={clubProfileId}
                workItem={currentWorkItem}
              />
            )}
            {currentView === 'manage-invite' && currentWorkItem && (
              <ManageWorkInviteViewContainer workItem={currentWorkItem} />
            )}
            {currentView === 'manage-contract' && currentWorkItem && (
              <ManageCurrentContractContainer workItem={currentWorkItem} />
            )}

            <CreateConversationButton
              recipientProfileId={clubProfileId}
              recipientProfileRole={Role.club}
            >
              <LBButton
                fullWidth
                leftSection={<EnvelopeIcon width={18} />}
                variant='outline'
              >
                {profile?.role === Role.club
                  ? translate('commons.askArtist')
                  : translate('commons.askClub')}
              </LBButton>
            </CreateConversationButton>
          </LBFixedSection>
        </div>
      ) : (
        <NotFoundWorkItem />
      )}
    </div>
  );
};
